/**
 * The five learner status labels, and the rules that project evidence onto
 * them.
 *
 * There are exactly five. There is no sixth, and there is no percentage
 * anywhere in this file or anywhere downstream of it (VISION principle 3).
 * `mixed_evidence` is an explanatory note attached to a status, never a
 * status of its own.
 */

export const LEARNER_STATUSES = [
  'not_tested',
  'learning',
  'can_use_with_help',
  'can_use_independently',
  'retained',
] as const;

export type LearnerStatus = (typeof LEARNER_STATUSES)[number];

/** Ordering used only to compare strength. Never shown to the learner. */
const STATUS_RANK: Readonly<Record<LearnerStatus, number>> = {
  not_tested: 0,
  learning: 1,
  can_use_with_help: 2,
  can_use_independently: 3,
  retained: 4,
} as const;

export function isLearnerStatus(value: unknown): value is LearnerStatus {
  return typeof value === 'string' && (LEARNER_STATUSES as readonly string[]).includes(value);
}

export function compareStatus(a: LearnerStatus, b: LearnerStatus): number {
  return STATUS_RANK[a] - STATUS_RANK[b];
}

export function isAtLeast(status: LearnerStatus, floor: LearnerStatus): boolean {
  return STATUS_RANK[status] >= STATUS_RANK[floor];
}

/**
 * Delay thresholds, in hours.
 *
 * Both are versioned and experimental. They are values in the founder's own
 * study, not constants of nature, so they carry a policy version that is
 * recorded with every projection they produce.
 *
 * `retainedHours` was confirmed by the founder on 2026-08-01 (decision D7) and
 * is now LOCKED. It is chosen to match the delayed-retention interval used in
 * the literature cited by EVID-001 and EVID-002, so results stay comparable to
 * published findings.
 *
 * Do not edit these values to change behaviour. The threshold is stamped into
 * every status as it is derived, so records written before a change would mean
 * something different from those written after — and could not be recomputed,
 * because the qualifying observations were never scheduled. A change is a
 * protocol amendment: bump `version`, keep the old policy readable, and record
 * the amendment in docs/research_protocol.md. Never mutate a policy in place.
 */
export const DELAY_POLICY = {
  version: 'delay-policy@1',
  /** Below this, a retrieval is not "delayed" and cannot demonstrate retention. */
  delayedRetrievalHours: 24,
  /** Independent success this long after the latest prior learning opportunity. */
  retainedHours: 168,
} as const;

export const HOUR_IN_MS = 3_600_000;

export function hoursBetween(earlier: Date, later: Date): number {
  return (later.getTime() - earlier.getTime()) / HOUR_IN_MS;
}
